import { useEffect, useRef } from 'react'

/**
 * Keep two scrollable elements in sync proportionally, driven only by the side
 * the user is actively interacting with. Programmatic scrolls (e.g. auto-scroll
 * to bottom on new tokens) on the inactive side are ignored so they don't yank
 * the side the user is reading.
 *
 * @param isAutoScrollingA  Optional ref (from `useAutoScroll`) that is `true`
 *   while `refA`'s own auto/jump-to-live scroll animation is in flight. Scroll
 *   events fired during that animation are transient intermediate positions,
 *   not the user manually scrolling — syncing `refB` to each of them makes it
 *   jump in instant, jerky steps instead of a single settle once the
 *   animation lands. Same for `isAutoScrollingB`.
 */
export default function useSyncScroll(
  refA: React.RefObject<HTMLElement>,
  refB: React.RefObject<HTMLElement>,
  isAutoScrollingA?: React.RefObject<boolean>,
  isAutoScrollingB?: React.RefObject<boolean>
): void {
  const activeRef = useRef<'A' | 'B' | null>(null)

  useEffect(() => {
    const elA = refA.current
    const elB = refB.current
    if (!elA || !elB) return

    function syncScroll(source: HTMLElement, target: HTMLElement) {
      const maxSource = source.scrollHeight - source.clientHeight
      const maxTarget = target.scrollHeight - target.clientHeight
      if (maxSource <= 0 || maxTarget <= 0) return
      const ratio = source.scrollTop / maxSource
      const next = ratio * maxTarget
      if (Math.abs(target.scrollTop - next) < 1) return
      target.scrollTop = next
    }

    const setA = () => {
      activeRef.current = 'A'
    }
    const setB = () => {
      activeRef.current = 'B'
    }
    const onScrollA = () => {
      if (isAutoScrollingA?.current) return
      if (activeRef.current === 'A') syncScroll(elA, elB)
    }
    const onScrollB = () => {
      if (isAutoScrollingB?.current) return
      if (activeRef.current === 'B') syncScroll(elB, elA)
    }

    const userEvents = ['pointerenter', 'wheel', 'touchstart', 'mousedown', 'keydown'] as const
    userEvents.forEach((ev) => {
      elA.addEventListener(ev, setA, { passive: true })
      elB.addEventListener(ev, setB, { passive: true })
    })
    elA.addEventListener('scroll', onScrollA, { passive: true })
    elB.addEventListener('scroll', onScrollB, { passive: true })

    return () => {
      userEvents.forEach((ev) => {
        elA.removeEventListener(ev, setA)
        elB.removeEventListener(ev, setB)
      })
      elA.removeEventListener('scroll', onScrollA)
      elB.removeEventListener('scroll', onScrollB)
    }
  })
}
